/**
 * @description
 * InvoicesService provides methods for fetching invoiced sales and their associated
 * receivables from the backend API, as well as helpers for viewing sealed invoice PDFs.
 *
 * Consumed by:
 * - InvoicesComponent (`@app/sales/invoices/invoices.component`)
 *
 * Backend endpoints:
 * - GET `/sales/invoices`
 * - GET `/sales/order/{id}/justificatif/{format}` (via PrintService, called by InvoicesComponent)
 */

import { Injectable } from '@angular/core';

import { HttpClient, HttpParams } from '@angular/common/http';

import { Observable } from 'rxjs';

import { InvoicedSale, InvoicesQueryParams } from '@app/shared/_models/sales';
import { showError } from '@app/shared/util';

import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class InvoicesService {
  /**
   * Base server URL from environment configuration.
   */
  private readonly baseUrl = environment.server_url;

  constructor(private http: HttpClient) {}

  /**
   * Fetches invoiced sales matching the given query parameters.
   *
   * @param params Optional filter and pagination parameters.
   * @returns Observable of invoiced sales array.
   */
  getInvoices(params: InvoicesQueryParams = {}): Observable<InvoicedSale[]> {
    let httpParams = new HttpParams();

    if (params.partnerId !== undefined && params.partnerId !== null && params.partnerId > 0) {
      httpParams = httpParams.set('partner_id', params.partnerId.toString());
    }

    if (params.dateFrom) {
      httpParams = httpParams.set('date_from', params.dateFrom);
    }

    if (params.dateTo) {
      httpParams = httpParams.set('date_to', params.dateTo);
    }

    if (params.state && params.state !== 'ALL') {
      httpParams = httpParams.set('state', params.state);
    }

    if (params.docType && params.docType !== 'ALL') {
      httpParams = httpParams.set('doc_type', params.docType);
    }

    if (params.search && params.search.trim().length > 0) {
      httpParams = httpParams.set('search', params.search.trim());
    }

    if (params.limit !== undefined && params.limit !== null) {
      httpParams = httpParams.set('limit', params.limit.toString());
    }

    if (params.offset !== undefined && params.offset !== null) {
      httpParams = httpParams.set('offset', params.offset.toString());
    }

    return this.http.get<InvoicedSale[]>(`${this.baseUrl}/sales/invoices`, {
      params: httpParams
    });
  }

  /**
   * Opens a PDF in a new browser tab via an authenticated Blob request.
   *
   * @param pdf$ The PDF request (e.g. `PrintService.getOrderJustificatif$`).
   */
  openPdf(pdf$: Observable<Blob>): void {
    // Onglet ouvert AVANT la requête, pendant le clic : ouvert au retour du serveur, le bloqueur de fenêtres l'arrêterait.
    const win = window.open('', '_blank');
    if (win) {
      try {
        win.document.open();
        win.document.write('<!DOCTYPE html><html><head><title>Chargement de la facture…</title><style>html{background:#525659}body{margin:0;height:100vh;display:flex;align-items:center;justify-content:center;font-family:Roboto,Arial,sans-serif;color:#e8eaed;font-size:14px;}</style></head><body>Chargement de la facture…</body></html>');
        win.document.close();
      } catch {
        // Ignoré en cas de restriction de sécurité
      }
    }

    pdf$.subscribe({
      next: (blob: Blob) => {
        const fileBlob = blob.type ? blob : new Blob([blob], { type: 'application/pdf' });
        const fileUrl = URL.createObjectURL(fileBlob);
        if (win && !win.closed) {
          win.location.href = fileUrl;
        } else {
          window.open(fileUrl, '_blank');
        }
        setTimeout(() => URL.revokeObjectURL(fileUrl), 60000);
      },
      // Corps d'erreur en Blob lui aussi (responseType: 'blob') : le motif du serveur est dedans.
      error: async (err) => {
        if (win && !win.closed) {
          win.close();
        }
        const serverMsg = err.error instanceof Blob ? await err.error.text().catch(() => '') : err.error?.message || err.error;
        showError(`La facture n'a pas pu être générée.\nDétails : ${serverMsg || err.message || 'Erreur inconnue'}`, 'Facture non disponible');
      }
    });
  }
}
